import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useAuth } from '../../components/auth/AuthContext';
import AdminTabs from '../../components/admin/AdminTabs';
import { APIHOST } from '../../../src/common';
import { Conference } from '../../types/conference';
import { useApp } from '../../components/AppContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);


export default function ReportsPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('');
  const [conferences, setConferences] = useState<Conference[]>([]);
  const [reportData, setReportData] = useState<any>(null);
  const { setSnackbarInput } = useApp()

  const isAuthenticated = currentUser && (currentUser.userType === 'internal' || currentUser.userType === 'admin');

  useEffect(() => {
    if (isAuthenticated) {
      // User is authenticated with admin privileges
    } else {
      navigate('/admin');
    }
    setLoading(false);
  }, [navigate, isAuthenticated]);

  useEffect(() => {
    fetch(`${APIHOST}/api/conferences`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
        })
        .then(response => response.json())
        .then(data => {
          setConferences(data.data);
        })
        .catch(error => {
          console.error('Error fetching conferences:', error);
        });
  }, []);



  const handleReportTypeChange = (event: SelectChangeEvent) => {
    setReportType(event.target.value);
  };

  const handleConferenceChange = (event: SelectChangeEvent) => {
    setReportType(event.target.value);
    fetch(`${APIHOST}/api/conferences/${event.target.value}/availability`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
    })
      .then(response => response.json())
      .then(data => {
        setReportData(data);
        setSnackbarInput({message: 'Conference availability data fetched successfully.'});
      })
      .catch(error => {
        console.error('Error fetching conference availability:', error);
        setSnackbarInput({message: 'Error fetching conference availability.', severity: 'error'});
      });
  };

  // Process report data for Chart.js - one chart per hotel
  const getHotelsFromData = () => {
    if (!reportData) return [];
    return Object.entries(reportData).map(([hotelId, hotel]: [string, any]) => ({
      id: hotelId,
      data: hotel
    }));
  };

  const processHotelChartData = (hotelId: string, hotelData: any) => {
    // Extract all unique dates from all rooms in this hotel
    const allDates = new Set<string>();
    
    Object.values(hotelData.rooms).forEach((room: any) => {
      Object.keys(room.availability).forEach(date => allDates.add(date));
    });

    const sortedDates = Array.from(allDates).sort();

    // Create datasets for each room in this hotel
    const datasets: any[] = [];
    const reservedColors = [
      '#FF5722', '#9C27B0', '#607D8B', '#E91E63', '#673AB7'
    ];
    const availableColor = '#333333'; // Dark grey for all available

    let colorIndex = 0;

    Object.entries(hotelData.rooms).forEach(([roomId, room]: [string, any]) => {
      const reservedColor = reservedColors[colorIndex % reservedColors.length];
      colorIndex++;

      // Calculate percentage data based on allocated rooms
      const reservedPercentData = sortedDates.map(date => {
        const dayData = room.availability[date];
        if (!dayData || dayData.allocated === 0) return 0;
        return Math.round((dayData.reserved / dayData.allocated) * 100);
      });

      const availablePercentData = sortedDates.map(date => {
        const dayData = room.availability[date];
        if (!dayData || dayData.allocated === 0) return 0;
        return Math.round((dayData.available / dayData.allocated) * 100);
      });

      // Push reserved first (bottom), then available (top)
      datasets.push({
        label: `${room.name || `Room ${roomId}`} (Reserved)`,
        data: reservedPercentData,
        backgroundColor: reservedColor,
        stack: `room-${roomId}`,
      });

      datasets.push({
        label: `${room.name || `Room ${roomId}`} (Available)`,
        data: availablePercentData,
        backgroundColor: availableColor,
        stack: `room-${roomId}`,
      });
    });

    return {
      labels: sortedDates,
      datasets: datasets,
    };
  };

  const getChartOptions = (hotelName: string) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          filter: function(legendItem: any) {
            return !legendItem.text.includes('(Available)') && !legendItem.text.includes('70% Target');
          }
        }
      },
      title: {
        display: true,
        text: `${hotelName} - Room Availability by Date (% of Allocated)`,
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'Percentage of Allocated Rooms (%)',
        },
        ticks: {
          callback: function(value: any) {
            return value + '%';
          }
        }
      },
    },
  });


  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="warning">
          Please log in to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 4, mt: 8 }}>
      <Container maxWidth="lg">


        <AdminTabs currentPath="/admin/reports" />
        
        <Box sx={{ mt: 3 }}>
          <Paper sx={{ p: 3 }}>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="report-type-label">Report Type</InputLabel>
              <Select
                labelId="report-type-label"
                id="report-type-select"
                value={'conference-availability'}
                label="Report Type"
                onChange={handleReportTypeChange}
              >
                <MenuItem value="conference-availability">Conference Availability Report</MenuItem>
              </Select>
            </FormControl>


            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="report-type-label">Report Type</InputLabel>
              <Select
                labelId="report-type-label"
                id="report-type-select"
                value={reportType}
                label="Conference"
                onChange={handleConferenceChange}
              >
                {conferences.map((conference) => (
                  <MenuItem key={conference.documentId} value={conference.documentId}>
                    {conference.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* {reportType && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1">
                  Selected report type: <strong>{reportType}</strong>
                </Typography>
              </Box>
            )} */}

            {reportData && getHotelsFromData().length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h5" component="h3" sx={{ mb: 3 }}>
                  Room Availability Charts by Hotel
                </Typography>
                
                {getHotelsFromData().map(({ id: hotelId, data: hotelData }) => (
                  <Paper key={hotelId} sx={{ mb: 4, p: 3 }}>
                    <Typography variant="h6" component="h4" sx={{ mb: 2 }}>
                      {hotelData.name}
                    </Typography>
                    <Box sx={{ height: '400px' }}>
                      <Bar 
                        data={processHotelChartData(hotelId, hotelData)} 
                        options={{
                          ...getChartOptions(hotelData.name),
                          interaction: {
                            intersect: false,
                          },
                        }} 
                      />
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Paper>
        </Box>
        
      </Container>
    </Box>
  );
}